import Vue from 'vue'
import axios from 'axios'
import {getItemsFromFolder, folderIcons} from '../helpers/menu-helpers'
async function getMergedPaths(applicationPaths, markets) {
  const {default: sitemapMeta} = await import('../sitemap')

  return sitemapMeta.map(item => {
    const applicationPath = applicationPaths.find(
      el =>
        el.path == item.path && item.meta && item.meta.module && markets.includes(item.meta.module),
    )
    if (!applicationPath) {
      return {
        ...item,
      }
    }
    return {
      ...applicationPath,
      meta: item.meta,
    }
  })
}

export default {
  namespaced: true,
  state: {
    marketId: localStorage.getItem('marketId') || null,
    selectedPeriodId: localStorage.getItem('selectedPeriodId') || null,
    applicationName: localStorage.getItem('applicationName') || null,
    applicationOrigin: localStorage.getItem('applicationOrigin') || null,
    applicationRevision: localStorage.getItem('applicationRevision') || null,
    applicationVersion: localStorage.getItem('applicationVersion') || null,
    applicationConfiguration: localStorage.getItem('applicationConfiguration') || null,
    applicationPaths: JSON.parse(localStorage.getItem('applicationPaths')) || [],
    markets: JSON.parse(localStorage.getItem('markets')) || [],
    reportDate: kendo.toString(localStorage.getItem('reportDate') || new Date(), 'yyyy-MM-dd'),
    history: JSON.parse(localStorage.getItem('history')) || [],
  },
  actions: {
    async setConfiguration(
      {commit},
      {
        applicationName,
        applicationOrigin,
        applicationRevision,
        applicationVersion,
        applicationConfiguration,
        applicationPaths,
        markets,
      },
    ) {
      commit('SET_APPLICATION_CONFIGURATION', {
        applicationName,
        applicationOrigin,
        applicationRevision,
        applicationVersion,
        applicationConfiguration,
        markets,
      })

      const mergedPaths = await getMergedPaths(applicationPaths, markets)

      commit('SET_CLIENT_PATHS', {
        applicationPaths: mergedPaths,
      })
    },
    async setClientConfiguration({commit, getters}, {selectedClientId}) {
      const {data} = await axios.get('/api/ApplicationConfiguration/getApplicationPaths', {
        params: {
          clientId: selectedClientId,
        },
      })
      const applicationPaths = data

      const markets = getters['markets']
      const mergedPaths = await getMergedPaths(applicationPaths, markets)
      commit('SET_CLIENT_PATHS', {
        applicationPaths: mergedPaths,
      })

      const {default: router} = await import('../router.js')
      const {currentRoute} = router
      const routePermission = applicationPaths.find(item => item.path == currentRoute.path)

      if (routePermission && !routePermission.enable) {
        router.push('/')
      }
    },
    newContent({commit}, {message}) {
      return new Promise(resolve => {
        Vue.toasted.show(message, {
          position: 'bottom-right',
          duration: 60 * 1000,
          className: ['vue-toast'],
          action: [
            {
              icon: 'refresh',
              onClick: () => {
                resolve(commit('refresh'))
              },
            },
          ],
        })
      })
    },
    selectPeriod({commit}, {periodId}) {
      commit('setSelectedPeriod', {
        periodId,
      })
    },
    setMenuMarket({commit}, {marketId}) {
      commit('setMenuMarket', {
        marketId,
      })
    },
    changeReportDate({commit}, {selectedDate}) {
      commit('setReportDate', {
        selectedDate,
      })
    },
    cleanApplicationConfiguration({commit}) {
      commit('CLEAN_APPLICATION_CONFIGURATION')
    },
    addHistory({commit}, {path}) {
      commit('storeHistory', {path})
    },
  },
  mutations: {
    setSelectedPeriod(state, {periodId}) {
      state.selectedPeriodId = periodId
      localStorage.setItem('selectedPeriodId', periodId)
    },
    setMenuMarket(state, {marketId}) {
      state.marketId = marketId
      localStorage.setItem('marketId', marketId)
    },
    setReportDate(state, {selectedDate}) {
      let convertedDate = kendo.toString(selectedDate, 'yyyy-MM-dd')
      state.reportDate = convertedDate
      localStorage.setItem('reportDate', convertedDate)
    },
    refresh() {
      location.reload(true)
    },
    SET_APPLICATION_CONFIGURATION(
      state,
      {
        applicationName,
        applicationOrigin,
        applicationRevision,
        applicationVersion,
        applicationConfiguration,
        markets,
      },
    ) {
      state.applicationName = applicationName
      state.applicationOrigin = applicationOrigin
      state.applicationRevision = applicationRevision
      state.applicationVersion = applicationVersion
      state.applicationConfiguration = applicationConfiguration
      state.markets = markets

      localStorage.setItem('applicationName', applicationName)
      localStorage.setItem('applicationOrigin', applicationOrigin)
      localStorage.setItem('applicationRevision', applicationRevision)
      localStorage.setItem('applicationVersion', applicationVersion)
      localStorage.setItem('applicationConfiguration', applicationConfiguration)
      localStorage.setItem('markets', JSON.stringify(markets))
    },
    CLEAN_APPLICATION_CONFIGURATION(state) {
      state.applicationName = ''
      state.applicationOrigin = ''
      state.applicationRevision = ''
      state.applicationVersion = ''
      state.applicationConfiguration = ''
      state.applicationPaths = []
      state.markets = []
      state.history = []

      localStorage.removeItem('lastActivity')
      localStorage.removeItem('applicationName')
      localStorage.removeItem('applicationOrigin')
      localStorage.removeItem('applicationRevision')
      localStorage.removeItem('applicationVersion')
      localStorage.removeItem('applicationConfiguration')
      localStorage.removeItem('applicationPaths')
      localStorage.removeItem('markets')
      localStorage.removeItem('history')
    },
    SET_CLIENT_PATHS(state, {applicationPaths}) {
      state.applicationPaths = applicationPaths
      localStorage.setItem('applicationPaths', JSON.stringify(applicationPaths))
    },
    storeHistory(state, {path}) {
      if (path == '/') return
      state.history = [...new Set([path, ...state.history])].slice(0, 10)
      localStorage.setItem('history', JSON.stringify(state.history))
    },
  },
  getters: {
    selectedPeriodId(state) {
      return state.selectedPeriodId || -1
    },
    marketId(state) {
      return parseInt(state.marketId) || -1
    },
    applicationName: state => {
      if (!state.applicationName) return ''
      return state.applicationName
    },
    applicationOrigin: state => {
      if (!state.applicationOrigin) return ''
      return state.applicationOrigin
    },
    applicationRevision: state => {
      if (!state.applicationRevision) return ''
      return state.applicationRevision
    },
    applicationVersion: state => {
      if (!state.applicationVersion) return ''
      return state.applicationVersion
    },
    applicationConfiguration: state => {
      if (!state.applicationConfiguration) return ''
      return state.applicationConfiguration
    },
    reportDate(state) {
      return kendo.toString(state.reportDate || new Date(), 'yyyy-MM-dd')
    },
    markets: state => state.markets,
    applicationPaths: state => state.applicationPaths,
    enabledPaths: (state, _getters, _rootState, rootGetters) =>
      state.applicationPaths.filter(
        item =>
          item.enable ||
          (item.name == 'application-configuration' &&
            rootGetters['authentication/currentRole'] == 'SuperAdmin'),
      ),
    pathsForCurrentRole: (_state, getters, _rootState, rootGetters) => {
      const role = rootGetters['authentication/currentRole']
      const paths = getters.enabledPaths.filter(item => {
        const {requiresRole} = item.meta || {}

        if (requiresRole) {
          return requiresRole.includes(role)
        }

        return true
      })

      if (role == 'SuperAdmin' && paths.find(x => x.path == '/client/features') == null) {
        paths.push({
          path: '/client/features',
          name: 'client-configuration',
          meta: {
            text: 'Features',
            icon: 'settings_system_daydream',
            module: 'Base',
            requiresAuth: true,
            requiresRole: ['SuperAdmin'],
            readOnlyRoles: ['Admin', 'SuperUser', 'Guest', 'Standard'],
          },
        })
      }

      return paths
    },
    pathNamesForCurrentRole: (_state, getters) =>
      new Set(['', '/login', ...getters.pathsForCurrentRole.map(x => x.path)]),
    folders: (_state, getters) => {
      const firstPathForFolder = getters.pathsForCurrentRole.reduce((acc, item) => {
        const folder = item.path.match(/\/(.*)\//)[1].toUpperCase()
        if (!acc[folder]) {
          acc[folder] = item.path
        }
        return acc
      }, {})

      return Object.keys(firstPathForFolder).map(folder => ({
        icon: folderIcons[folder],
        folder,
        firstPath: firstPathForFolder[folder],
      }))
    },
    filteredItems: (_state, getters) => {
      const clientId = +localStorage.getItem('selectedClientId')
      const isMaster = clientId == 0
      const items = getters.folders.map(item => ({
        icon: 'keyboard_arrow_up',
        'icon-alt': 'keyboard_arrow_down',
        text: item.folder,
        folderIcon: item.icon,
        expanded: false,
        children: getItemsFromFolder(
          getters.pathsForCurrentRole,
          '/' + item.folder.toLowerCase(),
          isMaster,
        ),
      }))

      return [
        {
          icon: 'stars',
          text: 'Home',
          to: '/',
        },
        ...items,
      ]
    },
    visibleModules: (state, getters) => [
      ...new Set(getters.pathsForCurrentRole.map(item => item && item.meta.module).filter(Boolean)),
    ],
    hasProcessManager: (state, getters) => {
      return getters.pathsForCurrentRole.some(x => x.path === '/process/manager')
    },
    hasOrders: (state, getters) => {
      return getters.pathsForCurrentRole.some(x => x.path.endsWith('order'))
    },
    hasInstruction: (state, getters) => {
      return getters.pathsForCurrentRole.some(x => x.path === '/list/instruction')
    },
    history: state => state.history,
  },
}
